import { TooltipWrapper } from '@components/TooltipWrapper';
import type { PropGetItemKey } from 'uikit';
import { UserSelect } from 'uikit';

import type React from 'react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { MultiselectItem } from './MultiselectItem';
import { MultiselectValue } from './MultiselectValueItem';

import Styles from './Multiselect.module.css';

export interface IMultiselectItemInformer {
    value: string | ReactNode;
    itemId: number;
    className?: string;
    message?: string;
    showOnlyInItems?: boolean;
}

export interface IMultiselectItem {
    id: number | string;
    label: string;
    disabled?: boolean;
    hideRemoveIfDisabled?: boolean;
    type?: string;
    informer?: IMultiselectItemInformer;
    disabledMessage?: string;
}

interface IMultiselectProps {
    items: IMultiselectItem[];
    selectedItems: IMultiselectItem[];
    onChange: (selectedItems: IMultiselectItem[]) => void;
    label?: string;
    labelPosition?: 'left' | 'top';
    disabled?: boolean;
    status?: 'alert' | 'warning' | 'success' | undefined;
    required?: boolean;
    disabledIds?: (number | string)[];
    disabledItemOnHoverMessage?: string;
    hideRemoveIfDisabled?: boolean;
    multiselectRef?: React.RefObject<HTMLDivElement>;
    getItemKey?: PropGetItemKey<IMultiselectItem>;
    getItemLabel?: (item: any) => string;
    informers?: IMultiselectItemInformer[];
    placeholder?: string;
    additionalClassname?: string;
}

export const Multiselect: React.FC<IMultiselectProps> = (props) => {
    const {
        items,
        selectedItems = [],
        onChange,
        label,
        labelPosition,
        disabled,
        status,
        required,
        disabledIds,
        disabledItemOnHoverMessage,
        hideRemoveIfDisabled,
        multiselectRef,
        getItemKey,
        getItemLabel,
        informers,
        placeholder,
        additionalClassname,
    } = props;
    const selectInputRef = useRef<HTMLInputElement>(null);
    const ref = useRef<HTMLDivElement>(null);

    const actualSelectedItems = useMemo(
        () =>
            selectedItems?.map((i) => {
                const itemFromList = items?.find((item) => item.id === i.id);

                return {
                    ...i,
                    disabled: itemFromList?.disabled || disabledIds?.includes(i.id) || disabled,
                    disabledMessage: itemFromList?.disabledMessage || disabledItemOnHoverMessage,
                    hideRemoveIfDisabled,
                    informer: informers?.find((info) => info.itemId === (getItemKey ? getItemKey(i) : i.id)),
                };
            }),
        [
            disabled,
            disabledIds,
            disabledItemOnHoverMessage,
            getItemKey,
            hideRemoveIfDisabled,
            informers,
            items,
            selectedItems,
        ],
    );

    const actualItems = useMemo(
        () =>
            items?.map((i) => ({
                ...i,
                disabled: i?.disabled || disabledIds?.includes(i.id) || disabled,
                disabledMessage: i?.disabledMessage || disabledItemOnHoverMessage,
                hideRemoveIfDisabled,
                informer: informers?.find((info) => info.itemId === (getItemKey ? getItemKey(i) : i.id)),
            })),
        [disabled, disabledIds, disabledItemOnHoverMessage, getItemKey, hideRemoveIfDisabled, informers, items],
    );

    useEffect(() => {
        if (selectInputRef.current) {
            selectInputRef.current.disabled = true;
        }
    }, []);

    const onLocalChange = useCallback(
        ({ value }) => {
            onChange(value);
        },
        [onChange],
    );

    const value = !actualSelectedItems?.length ? null : actualSelectedItems;

    const MultiselectValueRenderer = useCallback(
        (args: { item: IMultiselectItem }) => {
            const { item } = args;

            return (
                <TooltipWrapper
                    direction="upCenter"
                    isHidden={!item.disabled || !item.disabledMessage}
                    message={item.disabledMessage}
                >
                    <MultiselectValue {...args} getItemLabel={getItemLabel} />;
                </TooltipWrapper>
            );
        },
        [getItemLabel],
    );

    return (
        <UserSelect
            className={`${Styles.multiselect} ${additionalClassname || ''}`}
            disabled={disabled}
            labelPosition={labelPosition}
            label={label}
            multiple
            onChange={onLocalChange}
            items={actualItems}
            value={value}
            renderValue={MultiselectValueRenderer}
            renderItem={MultiselectItem}
            size="s"
            status={status}
            required={required}
            inputRef={selectInputRef}
            ref={multiselectRef || ref}
            getItemKey={getItemKey}
            placeholder={placeholder}
        />
    );
};

Multiselect.displayName = 'Multiselect';
